import Vue from 'vue'
import Vuex from 'vuex'
import axios from 'axios'
import { EventBus } from '~/plugins/eventbus.js'
import Queue from '~/lib/queue.js'

const createStore = () => {
  return new Vuex.Store({
    state: {
      events: {},
      settings: {},
      linkedStores: [],
      vouchers: [],
      customers: {},
      billingcustomers: {},
      shopId: '',
      hostName: '',
      user: null,
      apiKey: null,
      clientid: null,
      clientName: null,
      clientIdNumer: null,
      manifestprinter: null,
      defaultDeliverymethod: null,
      posDeliveryActive: false,
      tabid: null,
      floating: false,
      floatingReceipt: null,
      selfService: false,
      contract: null,
      layouts: null,
      pos: null,
      view: null,
      baseDomain: '',
      localDomain: '',
      basketIds: {},
      basketItems: null,
      activeBasket: 1,
      activeDeliveries: {},
      payments: {},
      mobilepayPhoneNumber: null,
      mobilepayToken: null,
      activeOrder: null,
      imageIndex: 0,
      timeDiff: false,
      userData: null,
      joinChannel: null,
      editMode: false,
      openCategories: [],
      openResourcepools: [],
      unsavedChanges: false,
      locales: ['fi_FI', 'en_US', 'de_DE', 'ru_RU', 'sv_SE', 'it_IT'], // available langages
      locale: null, // current locale
      systemLocale: null, // system locale
      version: null, // current version
      countries: {},
      checklistShown: false,
      lastSeenMessageTs: 0,
      ecosystemVisited: false, // Holopayne
      showAllFields: false,
      unreadEcosystemPostCount: 0,
      sideMenuOpen: false,
      ecosystemModalOpen: false,
      heartbeatTs: Math.floor(Date.now() / 1000),
      _: {} // store for translations
    },
    mutations: {
      setSettings (state, data) {
        Vue.set(state.settings, data.shopId + '-' + data.locale, data.settings)
        // If there is no locale but a shopid is set, set default locale
        if(state.shopid !== '' && !state.locale && data.shopId === state.shopId) {
          state.locale = data.locale
        }
      },
      setEvent (state, data) {
        Vue.set(state.events, data.identifier, data.handler)
      },
      setCustomer (state, data) {
        Vue.set(state.customers, data.index, data.customer)
      },
      setBillingcustomer (state, data) {
        Vue.set(state.billingcustomers, data.index, data.billingcustomer)
      },
      setLinkedStores (state, data) {
        state.linkedStores = data.linkedStores
      },
      setVouchers (state, data) {
        state.vouchers = data.vouchers
      },
      setShopId (state, shopId) {
        state.shopId = shopId
      },
      setHostName (state, hostName) {
        state.hostName = hostName
      },
      setUser (state, user) {
        Vue.set(state, 'user', user)
      },
      setApiKey (state, apiKey) {
        Vue.set(state, 'apiKey', apiKey)
      },
      setClientId (state, clientid) {
        Vue.set(state, 'clientid', clientid)
      },
      setClientName (state, clientName) {
        Vue.set(state, 'clientName', clientName)
      },
      setClientIdNumber(state, clientIdNumber) {
        Vue.set(state, 'clientIdNumber', clientIdNumber)
      },
      setManifestPrinter(state, printer) {
        Vue.set(state, 'manifestprinter', printer)
      },
      setDefaultDeliverymethod(state, deliverymethod) {
        Vue.set(state, 'defaultDeliverymethod', deliverymethod)
      },
      setPosDeliveryActive(state, status) {
        Vue.set(state, 'posDeliveryActive', status)
      },
      setTabId (state, tabid) {
        state.tabid = tabid
      },
      setFloating (state, floating) {
        Vue.set(state, 'floating', floating)
      },
      setFloatingReceipt (state, floatingReceipt) {
        Vue.set(state, 'floatingReceipt', floatingReceipt)
      },
      setSelfService (state, selfService) {
        Vue.set(state, 'selfService', selfService)
      },
      setContract (state, contract) {
        Vue.set(state, 'contract', contract)
      },
      setActiveOrder (state, order) {
        state.activeOrder = order
      },
      setLayouts (state, layouts) {
        state.layouts = layouts
      },
      setPos (state, pos) {
        Vue.set(state, 'pos', pos)
      },
      setView (state, view) {
        state.view = view
      },
      setBaseDomain (state, baseDomain) {
        state.baseDomain = baseDomain
      },
      setLocalDomain (state, localDomain) {
        state.localDomain = localDomain
      },
      setLocale (state, locale) {
        state.locale = locale
        state.systemLocale = locale
      },
      setUserLocale (state, locale) {
        state.locale = locale
      },
      setVersion (state, version) {
        state.version = version
      },
      setUserData (state, userData) {
        state.userData = userData
        if (userData && userData.language) {
          state.locale = userData.language
        } else {
          state.locale = state.systemLocale
        }
        if (userData && userData.advanced) {
          state.showAllFields = true
        } else {
          state.showAllFields = false
        }
      },
      setJoinChannel (state, joinChannel) {
        state.joinChannel = joinChannel
      },
      setCountries (state, countries) {
        state.countries[state.locale] = countries
      },
      setTranslation (state, translation) {
        state._[state.locale] = translation
      },
      resetBaskets (state) {
        state.basketIds = {}
      },
      setBasketIds (state, data) {
        Vue.set(state.basketIds, data.shopId + '-' + data.index, data.basketId)
      },
      setBasketItems (state, data) {
        if (state.basketItems === null) {
          state.basketItems = {}
        }
        Vue.set(state.basketItems, data.shopId + '-' + data.index, data.items)
      },
      setActiveBasket (state, basketIndex) {
        state.activeBasket = basketIndex
      },
      setPayments (state, data) {
        if (state.payments === null) {
          state.payments = {}
        }
        Vue.set(state.payments, data.shopId + '-' + data.index, data.payments)
      },
      setMobilepayPhoneNumber (state, phoneNumber) {
        state.mobilepayPhoneNumber = phoneNumber
      },
      setMobilepayToken (state, token) {
        state.mobilepayToken = token
      },
      setActiveDelivery (state, data) {
        Vue.set(state.activeDeliveries, data.shopId + '-' + data.index, data.deliveryId)
      },
      setImageIndex (state, index) {
        state.imageIndex = index
      },
      setEditMode (state, mode) {
        state.editMode = mode
      },
      setOpenCategories (state, oc) {
        state.openCategories = oc
      },
      setOpenResourcepools (state, orp) {
        state.openResourcepools = orp
      },
      setUnsavedChanges (state, unsavedChanges) {
        state.unsavedChanges = unsavedChanges
      },
      setChecklistShown (state, value) {
        state.checklistShown = value
      },
      setLastSeenMessageTs (state, value) {
        state.lastSeenMessageTs = value
      },
      setEcosystemVisited(state, value) {
        state.ecosystemVisited = value
      },
      setUnreadEcosystemPostCount(state, value) {
        state.unreadEcosystemPostCount = value
      },
      setSideMenuOpen(state, value) {
        state.sideMenuOpen = value
      },
      setEcosystemModalOpen(state, value) {
        state.ecosystemModalOpen = value
      },
      setShowAllFields(state, value) {
        state.showAllFields = parseInt(value)
      },
      setHeartbeatTs(state, value) {
        state.heartbeatTs = value
      }
    },
    getters: {
      allSettings (state) {
        return state.settings
      },
      events (state) {
        return state.events
      },
      allCustomers (state) {
        return state.customers
      },
      allBillingcustomers (state) {
        return state.billingcustomers
      },
      linkedStores (state) {
        return state.linkedStores
      },
      vouchers (state) {
        return state.vouchers
      },
      shopId (state) {
        return state.shopId
      },
      hostName (state) {
        return state.hostName
      },
      user (state) {
        return state.user
      },
      apiKey (state) {
        return state.apiKey
      },
      clientid (state) {
        return state.clientid
      },
      clientName (state) {
        return state.clientName
      },
      clientIdNumber (state) {
        return state.clientIdNumber
      },
      manifestprinter (state) {
        return state.manifestprinter
      },
      defaultDeliverymethod (state) {
        return state.defaultDeliverymethod
      },
      posDeliveryActive (state) {
        return state.posDeliveryActive
      },
      posDeliveryAvailable (state) {
        return !!state.defaultDeliverymethod
      },
      tabid (state) {
        return state.tabid
      },
      floating (state) {
        return state.floating
      },
      floatingReceipt (state) {
        return state.floatingReceipt
      },
      selfService (state) {
        return state.selfService
      },
      contract (state) {
        return state.contract
      },
      activeOrder (state) {
        return state.activeOrder
      },
      layouts (state) {
        return state.layouts
      },
      pos (state) {
        return state.pos
      },
      view (state) {
        return state.view
      },
      baseDomain (state) {
        return state.baseDomain
      },
      localDomain (state) {
        return state.localDomain
      },
      locale (state) {
        return state.locale
      },
      version (state) {
        return state.version ? state.version : state.settings && state.shopId && state.locale && state.settings[state.shopId + '-' + state.locale] ? state.settings[state.shopId + '-' + state.locale].defaultversion : null
      },
      userData (state) {
        return state.userData
      },
      joinChannel (state) {
        return state.joinChannel
      },
      basketIds (state) {
        return state.basketIds
      },
      basketItems (state) {
        return state.basketItems
      },
      payments (state) {
        return state.payments
      },
      mobilepayPhoneNumber (state) {
        return state.mobilepayPhoneNumber
      },
      mobilepayToken (state) {
        return state.mobilepayToken
      },
      activeBasket (state) {
        return state.activeBasket
      },
      activeDelivery (state) {
        return state.activeDeliveries
      },
      timeDiff (state) {
        return state.timeDiff
      },
      editMode (state) {
        return state.editMode
      },
      countries (state) {
        return state.countries[state.locale]
      },
      openCategories (state) {
        return state.openCategories
      },
      openResourcepools (state) {
        return state.openResourcepools
      },
      unsavedChanges (state) {
        return state.unsavedChanges
      },
      checklistShown (state) {
        return state.checklistShown
      },
      lastSeenMessageTs (state) {
        return state.lastSeenMessageTs
      },
      ecosystemVisited (state) {
        return state.ecosystemVisited
      },
      unreadEcosystemPostCount (state) {
        return state.unreadEcosystemPostCount
      },
      sideMenuOpen(state) {
        return state.sideMenuOpen
      },
      ecosystemModalOpen(state) {
        return state.ecosystemModalOpen
      },
      showAllFields(state) {
        return state.showAllFields
      },
      heartbeatTs(state) {
        return state.heartbeatTs
      },
      dateFormat (state) {
        if (state.userData && state.userData.dateformat) {
          return state.userData.dateformat
        }
        if (state.settings && state.shopId && state.locale && state.settings[state.shopId + '-' + state.locale] && state.settings[state.shopId + '-' + state.locale].dateformat) {
          return state.settings[state.shopId + '-' + state.locale].dateformat
        }
        if (state._ && state.locale && state._[state.locale] && state._[state.locale]['YYYY/MM/DD']) {
          return state._[state.locale]['YYYY/MM/DD']
        }
        return 'D.M.YYYY'
      },
      timeFormat (state) {
        if (state.userData && state.userData.timeformat) {
          return state.userData.timeformat
        }
        if (state.settings && state.shopId && state.locale && state.settings[state.shopId + '-' + state.locale] && state.settings[state.shopId + '-' + state.locale].timeformat) {
          return state.settings[state.shopId + '-' + state.locale].timeformat
        }
        if (state._ && state.locale && state._[state.locale] && state._[state.locale]['h.mm A']) {
          return state._[state.locale]['h.mm A']
        }
        return 'H.mm'
      },
      numberFormat (state) {
        if (state.userData && state.userData.numberformat) {
          return state.userData.numberformat
        }
        if (state.settings && state.shopId && state.locale && state.settings[state.shopId + '-' + state.locale] && state.settings[state.shopId + '-' + state.locale].numberformat) {
          return state.settings[state.shopId + '-' + state.locale].numberformat
        }
        return 'fi-FI'
      }
    },
    actions: {
      nuxtServerInit({ commit }, { req }) {
        if (req.session && req.session.user) {
          commit('setUser', req.session.user)
        }
        if (req.session && req.session.apiKey) {
          commit('setApiKey', req.session.apiKey)
        }
        if (req.session && req.session.clientid) {
          commit('setClientId', req.session.clientid)
        }
      },
      setClientName ({ state, commit }, clientName) {
        commit('setClientName', clientName)
      },
      setClientIdNumber ({ state, commit }, clientIdNumber) {
        commit('setClientIdNumber', clientIdNumber)
      },
      setManifestPrinter ({ state, commit }, printer) {
        commit('setManifestPrinter', printer)
      },
      setDefaultDeliverymethod ({ state, commit }, deliverymethod) {
        commit('setDefaultDeliverymethod', deliverymethod)
      },
      setPosDeliveryActive ({ state, commit }, status) {
        commit('setPosDeliveryActive', status)
      },
      setTabId ({ state, commit }, tabid) {
        commit('setTabId', tabid)
      },
      setFloating ({ state, commit }, floating) {
        commit('setFloating', floating)
      },
      setFloatingReceipt ({ state, commit }, floatingReceipt) {
        commit('setFloatingReceipt', floatingReceipt)
      },
      setSelfService ({ state, commit }, selfService) {
        commit('setSelfService', selfService)
      },
      setContract ({ state, commit }, contract) {
        commit('setContract', contract)
      },
      setActiveOrder ({ state, commit }, order) {
        commit('setActiveOrder', order)
      },
      setLayouts ({ state, commit }, layouts) {
        commit('setLayouts', layouts)
      },
      setPos ({ state, commit }, pos) {
        commit('setPos', pos)
      },
      setView ({ state, commit }, view) {
        commit('setView', view)
      },
      setTimeDiff ({ state, commit }, diff) {
        commit('setTimeDiff', diff)
      },
      setHostName ({ state, commit }, hostName) {
        commit('setHostName', hostName)
      },
      setBaseDomain ({ state, commit }, baseDomain) {
        commit('setBaseDomain', baseDomain)
      },
      setLocalDomain ({ state, commit }, localDomain) {
        commit('setLocalDomain', localDomain)
      },
      setShopId ({ state, commit }, shopId) {
        commit('setShopId', shopId)
      },
      setSettings ({ state, commit }, data) {
        commit('setSettings', data)
      },
      setEvent ({ state, commit }, data) {
        commit('setEvent', data)
      },
      setCustomer ({ state, commit }, data) {
        commit('setCustomer', data)
      },
      setBillingcustomer ({ state, commit }, data) {
        commit('setBillingcustomer', data)
      },
      setLinkedStores ({ state, commit }, data) {
        // await axios.post(state.localDomain + 'linkedstores', data)
        commit('setLinkedStores', data)
      },
      setVouchers ({ state, commit }, data) {
        commit('setVouchers', data)
      },
      setActiveBasket ({ state, commit }, data) {
        commit('setActiveBasket', data)
      },
      setImageIndex ({ state, commit }, index) {
        commit('setImageIndex', index)
      },
      toggleEditMode({ state, commit }) {
        commit('setEditMode', !state.editMode)
      },
      setOpenCategories({ state, commit }, data) {
        commit('setOpenCategories', data)
      },
      setOpenResourcepools({ state, commit }, data) {
        commit('setOpenResourcepools', data)
      },
      setUnsavedChanges({ state, commit }, data) {
        commit('setUnsavedChanges', data)
      },
      setChecklistShown({ state, commit }, data) {
        commit('setChecklistShown', data)
      },
      setLastSeenMessageTs({ state, commit }, data) {
        commit('setLastSeenMessageTs', data)
      },
      setEcosystemVisited({ state, commit }, data) {
        commit('setEcosystemVisited', data)
      },
      setUnreadEcosystemPostCount({ state, commit }, data) {
        commit('setUnreadEcosystemPostCount', data)
      },
      setSideMenuOpen({ state, commit }, data) {
        commit('setSideMenuOpen', data)
      },
      setEcosystemModalOpen({ state, commit }, data) {
        commit('setEcosystemModalOpen', data)
      },
      setShowAllFields({ state, commit }, data) {
        commit('setShowAllFields', data)
      },
      setHeartbeatTs({ state, commit }, data) {
        commit('setHeartbeatTs', Math.floor(Date.now() / 1000))
      },
      async reloadSettings ({ state, dispatch }) {
        let pubStr = '/public.json?'
        if (state.apiKey) {
          pubStr = '.json?apikey=' + state.apiKey + '&'
        }
        let localeStr = ''
        if (state.locale) {
          localeStr = '/' + state.locale
        }
        return axios.all([
          axios.get(state.baseDomain + state.shopId + localeStr + '/settings' + pubStr + 'version=' + state.locale + '&pricetiers=true&files=true&alltags=true' + (state.apiKey ? '&deliverymethods=true' : ''))
          .catch(() => {
            console.log(state.shopId, 'Settings not found')
            dispatch('setSettings', {
              shopId: state.shopId,
              locale: state.locale,
              settings: {}
            })
          }),
          axios.get(state.baseDomain + state.shopId + localeStr + '/linkedstores/public.json?version=' + state.locale)
          .catch(() => {
            console.log(state.shopId, 'Linked store not found')
            dispatch('setLinkedStores', {
              linkedStores: []
            })
          }),
          axios.get(state.baseDomain + state.shopId + localeStr + '/vouchers.json?iSortingCols=1&iSortDir_0=2')
          .catch(() => {
            console.log(state.shopId, 'Vouchers not found')
            dispatch('setVouchers', {
              vouchers: []
            })
          })
        ])
        .then(axios.spread(async function (settings, linkedStores, vouchers) {
          dispatch('setSettings', {
            shopId: state.shopId,
            locale: state.locale,
            settings: settings.data
          })
          dispatch('setLinkedStores', {
            linkedStores: linkedStores.data
          })
          dispatch('setVouchers', {
            vouchers: vouchers.data
          })
          if (linkedStores && linkedStores.data && linkedStores.data.length) {
            for (var i = 0; i < linkedStores.data.length; i++) {
              let linkedStoreShopId = linkedStores.data[i].shopid
              await axios.get(state.baseDomain + linkedStoreShopId + localeStr + '/settings/public.json?version=' + state.locale + '&pricetiers=true')
              .then(function (linkedSettings) {
                dispatch('setSettings', {
                  shopId: linkedStoreShopId,
                  locale: state.locale,
                  settings: linkedSettings.data
                })
              })
            }
          }
        }))
        .catch(() => {
          console.log(state.shopId, 'Error loading settings.')
          dispatch('setSettings', {
            shopId: state.shopId,
            locale: locale,
            settings: {}
          })
        })
      },
      async login({ state, commit, dispatch }, { email, password }) {
        try {
          const { data } = await axios.post(state.localDomain + 'login', { email: email, password: password, shopId: state.shopId, clientid: state.clientid })
          commit('setUser', data.user)
          commit('setApiKey', data.apiKey)
          dispatch('reloadSettings')
        } catch (error) {
          if (error.response && error.response.status === 401) {
            throw new Error('Bad credentials')
          }
          throw error
        }
      },
      async apilogin({ state, commit, dispatch }, { apikey }) {
        try {
          const { data } = await axios.post(state.localDomain + 'login', { apikey: apikey, shopId: state.shopId, clientid: state.clientid })
          commit('setUser', data.user)
          commit('setApiKey', data.apiKey)
          dispatch('reloadSettings')
        } catch (error) {
          if (error.response && error.response.status === 401) {
            throw new Error('Bad credentials')
          }
          throw error
        }
      },
      async logout({ state, commit, dispatch }) {
        try {
          await axios.post(state.localDomain + 'logout', { shopId: state.shopId, clientid: state.clientid, user: state.user })
        } catch(e) {}
        commit('setUser', null)
        commit('setApiKey', null)
        commit('setUserData', null)
        commit('setJoinChannel', null)
        commit('setClientName', null)
        commit('setClientIdNumber', null)
        commit('setManifestPrinter', null)
        commit('setFloating', null)
        commit('setFloatingReceipt', null)
        commit('setSelfService', null)
        commit('setContract', null)
        commit('setPos', null)
        commit('resetBaskets')
        commit('setLinkedStores', {
          linkedStores: []
        })
        dispatch('reloadSettings')
      },
      async clientid({ state, commit }, input) {
        try {
          if (state.localDomain && state.shopId) {
            const params = { shopId: state.shopId }
            if (input && input.clientid) {
              params.clientid = input.clientid
            }
            const { data } = await axios.post(state.localDomain + 'clientid', params)
            if (data.clientid) {
              commit('setClientId', data.clientid)
            }
          }
        } catch(e) {
          console.log(e)
        }
      },
      async userdata({ state, commit }, reload) {
        if (!reload && state.userData) {
          return
        }
        try {
          const { data } = await axios.get(state.baseDomain + state.shopId + '/users/' + state.user + '.json?apikey=' + state.apiKey)
          commit('setUserData', data)
        } catch(e) {}
      },
      async joinchannel({ commit }, data) {
        commit('setJoinChannel', data)
      },
      async setLocale ({ state, commit }, locale) {
        if (state._[locale]) {
          return commit('setLocale', locale)
        }
        try {
          let res = await axios.get(`${state.baseDomain}include/locale/pos-${locale}.json`)
          commit('setLocale', locale)
          commit('setTranslation', res.data)
          res = await axios.get(`${state.baseDomain}include/locale/countries-${locale}.json`)
          commit('setCountries', res.data)
        } catch(e) {}
      },
      async setUserLocale ({ state, commit }, locale) {
        if (state._[locale]) {
          return commit('setUserLocale', locale)
        }
        try {
          let res = await axios.get(`${state.baseDomain}include/locale/pos-${locale}.json`)
          commit('setUserLocale', locale)
          commit('setTranslation', res.data)
          res = await axios.get(`${state.baseDomain}include/locale/countries-${locale}.json`)
          commit('setCountries', res.data)
        } catch(e) {}
      },
      setVersion ({ commit }, version) {
        commit('setVersion', version)
      },
      resetBaskets ({ commit }) {
        commit('resetBaskets')
      },
      async setBasketIds ({ state, commit }, data) {
        let localeStr = ''
        if (state.locale) {
          localeStr = '/' + state.locale
        }
        let extraStr = ''
        if (data.shopId !== state.shopId) {
          extraStr += '&shop=' + state.shopId
        }
        if (state.activeOrder) {
          extraStr += '&orderidentifier=' + state.activeOrder.identifier
        }
        let res = { data: [] }
        try {
          res = await axios.get(`${state.baseDomain}${data.shopId}${localeStr}/baskets/${data.basketId}/items.json?client=${state.clientid}${extraStr}`)
        } catch(e) {}
        commit('setBasketIds', data)
        commit('setBasketItems', { shopId: data.shopId, index: data.index, items: res.data })
      },
      setActiveDelivery ({ commit }, data) {
        commit('setActiveDelivery', { shopId: data.shopId, index: data.index, deliveryId: data.deliveryId })
      },
      async reloadBasket ({ state, commit }, basketShopIdWithIndex) {
        let localeStr = ''
        if (state.locale) {
          localeStr = '/' + state.locale
        }
        for (var shopIdWithIndex in state.basketIds) {
          let siArr = shopIdWithIndex.split('-')
          let shopId = siArr[0]
          let index = siArr[1]
          if (Object.prototype.hasOwnProperty.call(state.basketIds, shopIdWithIndex) && (!basketShopIdWithIndex || shopIdWithIndex === basketShopIdWithIndex)) {
            let extraStr = ''
            if (shopId !== state.shopId) {
              extraStr += '&shop=' + state.shopId
            }
            if (state.activeOrder) {
              extraStr += '&orderidentifier=' + state.activeOrder.identifier
            }
            try {
              let res = await axios.get(`${state.baseDomain}${shopId}${localeStr}/baskets/${state.basketIds[shopIdWithIndex]}/items.json?client=${state.clientid}${extraStr}`)
              commit('setBasketItems', { shopId: shopId, index: index, items: res.data })
              EventBus.$emit('setBasketItems')
            } catch(e) {}
          }
        }
      },
      async setBasketItems({ state, commit }, data) {
        commit('setBasketItems', data)
      },
      async reloadPayments ({ state, commit }, basketShopIdWithIndex) {
        let response
        let siArr = basketShopIdWithIndex.split('-')
        let shopId = siArr[0]
        let index = siArr[1]
        try {
          let extraStr = ''
          if (state.activeOrder) {
            extraStr += '&orderidentifier=' + state.activeOrder.identifier
          }
          response = await axios.get(state.baseDomain + shopId + '/baskets/' + state.basketIds[basketShopIdWithIndex] + '/pospayments.json?apikey=' + state.apiKey + '&client=' + state.clientid + extraStr)
        } catch(e) {
          console.log('error reloading payments', e)
          return
        }
        if (response && response.data) {
          commit('setPayments', { shopId: shopId, index: index, payments: response.data })
        } else {
          commit('setPayments', {})
        }
      },
      setPayments ({ commit }, data) {
        commit('setPayments', data)
        EventBus.$emit('setPayments')
      },
      setMobilepayPhoneNumber ({ commit }, phoneNumber) {
        commit('setMobilepayPhoneNumber', phoneNumber)
      },
      setMobilepayToken ({ commit }, token) {
        commit('setMobilepayToken', token)
      },
      async deleteAllItemsFromBasket ({ state, commit }, data) {
        let identifier = data.shopId + '-' + state.activeBasket
        commit('setBasketItems', { shopId: data.shopId, index: state.activeBasket, items: [] })
        let extraStr = ''
        if (data.shopId !== state.shopId) {
          extraStr += '&shop=' + state.shopId
        }
        if (state.activeOrder) {
          extraStr += '&orderidentifier=' + state.activeOrder.identifier
        }
        try {
          await axios.get(`${state.baseDomain}${data.shopId}/rest/delete/baskets/${state.basketIds[identifier]}.json?client=${state.clientid}&apikey=${state.apiKey}${extraStr}&random=${Math.random()}`)
        } catch(e) {}
        EventBus.$emit('basketEmptied', {
          shopId: data.shopId,
          index: state.activeBasket
        })
        EventBus.$emit('setBasketItems')
      },
      async storeShowAllFields ({ state, commit }, data) {
        if(!state.userData) {
          return
        }
        console.log("Set show all fields")
        console.log(data)
        commit('setShowAllFields', data)
        console.log("Kloonaa")
        const clonedUserData = structuredClone(state.userData)
        clonedUserData.advanced = parseInt(data)
        console.log("Puttaa")
        try {
          await axios.put(`${state.baseDomain}${state.shopId}/users/${state.userData.id}?&apikey=${state.apiKey}`, clonedUserData)
        } catch(e) {
          console.log("Error")
          if(e.response) {
            console.log(e.response)
          } else {
            console.log(e)
          }
        }
      },
      async deleteItemFromBasket ({ state, commit }, data) {
        let identifier = data.shopId + '-' + state.activeBasket
        let tmpItems = state.basketItems[identifier]
        for (var i = 0; i < tmpItems.length; i++) {
          if (tmpItems[i].id == data.id) {
            tmpItems.splice(i, 1)
            break
          }
        }
        commit('setBasketItems', { shopId: data.shopId, index: state.activeBasket, items: tmpItems })
        let extraStr = ''
        if (data.shopId !== state.shopId) {
          extraStr += '&shop=' + state.shopId
        }
        if (state.activeOrder) {
          extraStr += '&orderidentifier=' + state.activeOrder.identifier
        }
        try {
          await axios.get(`${state.baseDomain}${data.shopId}/rest/delete/baskets/${state.basketIds[identifier]}/items/${data.id}.json?client=${state.clientid}&apikey=${state.apiKey}${extraStr}&random=${Math.random()}`)
        } catch(e) {}
        EventBus.$emit('removedFromBasket', {
          item: data.item,
          shopId: data.shopId,
          index: state.activeBasket
        })
        EventBus.$emit('setBasketItems')
      },
      async unlinkBasketItems ({ state, commit }, data) {
        let identifier = data.shopId + '-' + state.activeBasket
        let tmpItems = state.basketItems[identifier]
        commit('setBasketItems', { shopId: data.shopId, index: state.activeBasket, items: [] })
        let extraStr = ''
        if (data.shopId !== state.shopId) {
          extraStr += '&shop=' + state.shopId
        }
        if (state.activeOrder) {
          extraStr += '&orderidentifier=' + state.activeOrder.identifier
        }
        let urlArray = []
        for (var i = 0; i < tmpItems.length; i++) {
          let item = tmpItems[i]
          if (item.order_id) {
            urlArray.push(axios.get(`${state.baseDomain}${data.shopId}/rest/patch/baskets/${state.basketIds[identifier]}/items/${item.id}.json?id=${item.id}&client=${state.clientid}&apikey=${state.apiKey}${extraStr}&random=${Math.random()}`))
          } else {
            try {
              await axios.get(`${state.baseDomain}${data.shopId}/rest/delete/baskets/${state.basketIds[identifier]}/items/${item.id}.json?client=${state.clientid}${extraStr}&random=${Math.random()}`)
            } catch(e) {}
          }
        }
        axios.all(urlArray)
        .then(axios.spread(function () {
          for (var i = 0; i < arguments.length; i++) {
            let item = tmpItems[i]
            EventBus.$emit('removedFromBasket', {
              item: item,
              shopId: data.shopId,
              index: state.activeBasket
            })
          }
          EventBus.$emit('setBasketItems')
        }))
      },
      async modifyBasketItem ({ state, commit }, data) {
        let identifier = data.shopId + '-' + state.activeBasket
        return new Promise((resolve, reject) => {
          let dataArr = []
          dataArr.push('id=' + data.id)
          dataArr.push('count=' + data.count)
          dataArr.push('price=' + String(data.price).replace(',', '.'))
          dataArr.push('client=' + state.clientid)
          if (data.masterShopId) {
            dataArr.push('shop=' + data.masterShopId)
          }
          if (data.product_id) {
            dataArr.push('product_id=' + data.product_id)
          }
          if (data.split) {
            dataArr.push('split=' + data.split)
          }
          if (data.hasOwnProperty('vat')) {
            dataArr.push('vat=' + data.vat)
          }
          if (data.hasOwnProperty('productName')) {
            dataArr.push('product_name=' + encodeURIComponent(data.productName))
          }
          if (data.hasOwnProperty('special') && data.special !== null) {
            dataArr.push('special=' + encodeURIComponent(data.special))
          }
          if (data.hasOwnProperty('netvisorAccount')) {
            dataArr.push('netvisoraccount=' + data.netvisorAccount)
          }
          if (data.hasOwnProperty('netvisorVatcode')) {
            dataArr.push('netvisorvatcode=' + data.netvisorVatcode)
          }
          if (data.hasOwnProperty('loyalty')) {
            dataArr.push('loyalty=' + data.loyalty)
          }
          if (data.hasOwnProperty('deliveryId')) {
            dataArr.push('delivery_id=' + data.deliveryId)
          }
          if (state.activeOrder) {
            dataArr.push('orderidentifier=' + state.activeOrder.identifier)
          }
          let dataStr = dataArr.join('&')
          axios.get(`${state.baseDomain}${data.shopId}/${data.locale}/rest/put/baskets/${state.basketIds[identifier]}/items/${data.id}.json?${dataStr}&apikey=${state.apiKey}&random=${Math.random()}`)
          .then(function (res) {
            resolve()
          })
          .catch(function (err) {
            let shopStr = ''
            if (data.shopId !== state.shopId) {
              shopStr = '&shop=' + state.shopId
            }
            axios.get(`${state.baseDomain}${data.shopId}/${data.locale}/baskets/${state.basketIds[identifier]}/items.json?client=${state.clientid}${shopStr}`)
            .then(function (items) {
              commit('setBasketItems', { shopId: data.shopId, index: state.activeBasket, items: items.data })
              EventBus.$emit('setBasketItems')
              if (err.response) {
                reject(err.response.data)
              } else {
                reject()
              }
            })
          })
        })
      },
      async addToBasket ({ state, commit }, data) {
        // Create a fake item and push it tentatively to the basketItems array. The basket will be updated with real data once the operation completes.
        let identifier = data.shopId + '-' + state.activeBasket
        let tmpItems = state.basketItems[identifier]
        let i = Object.assign({}, data)
        if (data.product) {
          i.product_id = data.product.id
          i.product_name = data.product.marketingname && parseInt(data.product.usemarketingname) ? data.product.marketingname : data.product.name
          i.count = data.count
          i.vat = data.product.vat
          i.currency = data.product.currency
          i.price = parseFloat(String(data.product.price).replace(',','.'))
          tmpItems.push(i)
        }
        commit('setBasketItems', { shopId: data.shopId, index: state.activeBasket, items: tmpItems })
        return Queue.enqueue(() => new Promise(function (resolve, reject) {
          let dataArr = []
          if (state.posDeliveryActive) {
            dataArr.push('delivery_id=pos')
          } else {
            dataArr.push('delivery_id=auto')
          }
          if (data.shopId !== state.shopId) {
            dataArr.push('shop=' + state.shopId)
          }
          dataArr.push('client=' + state.clientid)
          dataArr.push('version=' + (data.version ? data.version : data.locale))
          if (data.product) {
            dataArr.push('product_id=' + data.product.id)
          } else if (data.productName) {
            dataArr.push('product_name=' + encodeURIComponent(data.productName))
          }
          if (data.hasOwnProperty('price')) {
            dataArr.push('price=' + String(data.price).replace(',','.'))
          }
          if (data.hasOwnProperty('vat')) {
            dataArr.push('vat=' + data.vat)
          }
          if (data.code) {
            dataArr.push('code=' + data.code)
          }
          if (data.item) {
            dataArr.push('item_id=' + data.item.id)
          }
          if (data.valuecard) {
            dataArr.push('valuecard=' + data.valuecard)
          }
          if (data.voucher) {
            dataArr.push('voucher=' + data.voucher)
          }
          if (data.amount) {
            dataArr.push('amount=' + data.amount)
          }
          if (data.showId) {
            dataArr.push('show_id=' + data.showId)
          }
          if (data.ticket) {
            dataArr.push('ticket=' + data.ticket)
            if (data.pricing) {
              dataArr.push('pricing=' + data.pricing)
            }
          }
          if (data.selectedSeats) {
            dataArr.push('seat_ids=' + data.selectedSeats)
          }
          if (data.count) {
            dataArr.push('count=' + data.count)
          }
          if (data.resourceCount) {
            dataArr.push('resource_count=' + data.resourceCount)
          }
          if (data.reservationCount) {
            dataArr.push('reservation_count=' + data.reservationCount)
          }
          if (data.reservationDateStart) {
            dataArr.push('reservation_datestart=' + data.reservationDateStart)
          }
          if (data.reservationDateEnd) {
            dataArr.push('reservation_dateend=' + data.reservationDateEnd)
          }
          if (data.reservationTimeStart) {
            dataArr.push('reservation_timestart=' + data.reservationTimeStart)
          }
          if (data.reservationTimeEnd) {
            dataArr.push('reservation_timeend=' + data.reservationTimeEnd)
          }
          if (data.maintenanceMode) {
            dataArr.push('maintenance_window=' + data.maintenanceMode)
          }
          if (data.origin) {
            dataArr.push('origin=' + data.origin)
          }
          if (data.destination) {
            dataArr.push('destination=' + data.destination)
          }
          if (data.roundTrip) {
            dataArr.push('roundtrip=1')
            dataArr.push('return_date=' + data.returnDate)
            dataArr.push('return_time=' + data.returnTime)
            dataArr.push('return_endtime=' + data.returnEndTime)
          }
          if (data.linkType) {
            dataArr.push('linktype=' + data.linkType)
          }
          if (data.hasOwnProperty('special')) {
            dataArr.push('special=' + encodeURIComponent(data.special))
          }
          if (data.hasOwnProperty('netvisorAccount')) {
            dataArr.push('netvisoraccount=' + data.netvisorAccount)
          }
          if (data.hasOwnProperty('netvisorVatcode')) {
            dataArr.push('netvisorvatcode=' + data.netvisorVatcode)
          }
          if (state.activeOrder) {
            dataArr.push('orderidentifier=' + state.activeOrder.identifier)
          }
          if (data.voucherPrice) {
            dataArr.push('price=' + data.voucherPrice.replace(',','.'))
          }
          if (data.resourcepools) {
            dataArr.push('resourcepools=' + data.resourcepools)
          }
          for (var key in data) {
            if (Object.prototype.hasOwnProperty.call(data, key)) {
              if (key.match(/^pricetier/) && parseInt(data[key])) {
                dataArr.push(key + '=' + data[key])
              }
              if (key.match(/^pool/) && parseInt(data[key])) {
                dataArr.push(key + '=' + data[key])
              }
            }
          }
          let dataStr = dataArr.join('&')
          let itemId = 'new'
          if (data.code && data.item_id) {
            itemId = data.item_id
          }
          axios.get(`${state.baseDomain}${data.shopId}/${data.locale}/rest/post/baskets/${state.basketIds[identifier]}/items/${itemId}.json?${dataStr}&apikey=${state.apiKey}&random=${Math.random()}`)
          .then(function (res) {
            if (data.product) {
              EventBus.$emit('addedToBasket', {
                product: data.product,
                shopId: data.shopId,
                index: state.activeBasket,
                count: data.count
              })
            } else if (data.code) {
              EventBus.$emit('addedToBasket', {
                code: data.code,
                id: data.id,
                shopId: data.shopId,
                index: state.activeBasket,
                count: 1
              })
            } else if (data.valuecard) {
              EventBus.$emit('addedToBasket', {
                valuecard: data.valuecard,
                id: data.id,
                shopId: data.shopId,
                index: state.activeBasket,
                count: data.count
              })
            } else if (data.voucher) {
              EventBus.$emit('addedToBasket', {
                voucher: data.voucher,
                id: data.id,
                shopId: data.shopId,
                index: state.activeBasket,
                count: data.count
              })
            }
            EventBus.$emit('setBasketItems')
            resolve()
          })
          .catch(function (err) {
            let extraStr = ''
            if (data.shopId !== state.shopId) {
              extraStr += '&shop=' + state.shopId
            }
            if (state.activeOrder) {
              extraStr += '&orderidentifier=' + state.activeOrder.identifier
            }
            axios.get(`${state.baseDomain}${data.shopId}/${data.locale}/baskets/${state.basketIds[identifier]}/items.json?client=${state.clientid}${extraStr}`)
            .then(function (items) {
              commit('setBasketItems', { shopId: data.shopId, index: state.activeBasket, items: items.data })
              EventBus.$emit('setBasketItems')
              if (err.response) {
                reject(err.response.data)
              } else {
                reject()
              }
            })
            .catch(function(e) {
              // In case the get also fails, let's return the previous request's error
              if (err.response) {
                reject(err.response.data)
              } else {
                reject()
              }
            })
          })
        }))
      }
    }
  })
}

export default createStore
